/**
 * Dashboard Service
 * Handles dashboard-related API calls
 */

import { apiClient } from "../client";
import type {
  DashboardData,
  DashboardMetrics,
  SalesData,
  AgingData,
  SalesmanAnalysis,
  SalesmanWiseMarginAnalysis,
  DashboardOnlineItem,
  DashboardExpiresResponse,
  DashboardOutstandingResponse,
  DashboardSalesPurchasesItem,
  DashboardSummaryResponse,
  DeadStockItem,
  MarginAnalysis,
  ManufacturerMargin,
  CustomerMargin,
  MarginSummaryValues,
  MarginTrendMonthlyPerformance,
  CreditAgingVsMargin,
  RenewDetailsResponse,
  PlanDetails,
} from "../types";

export const dashboardService = {
  /**
   * Get dashboard overview data
   */
  async getDashboardData(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<DashboardData> {
    const response = await apiClient.get<DashboardData>("/dashboard", params);
    return response.data;
  },

  /**
   * Get dashboard metrics
   */
  async getMetrics(params?: {
    startDate?: string;
    endDate?: string;
  }): Promise<DashboardMetrics> {
    const response = await apiClient.get<DashboardMetrics>("/dashboard/metrics", params);
    return response.data;
  },

  /**
   * Get sales data for charts
   */
  async getSalesData(params?: {
    startDate?: string;
    endDate?: string;
    period?: "monthly" | "weekly" | "daily";
  }): Promise<SalesData[]> {
    const response = await apiClient.get<SalesData[]>("/dashboard/sales", params);
    return response.data;
  },

  /**
   * Get aging data for charts
   */
  async getAgingData(): Promise<AgingData[]> {
    const response = await apiClient.get<AgingData[]>("/dashboard/aging");
    return response.data;
  },

  async getSalesmanAnalysis(params?: { firmId?: number }): Promise<SalesmanAnalysis[]> {
    const response = await apiClient.get<SalesmanAnalysis[]>("DirectorModule/salesman-wise-analysis", params);
    return response.data;
  },

  async getSalesmanWiseMarginAnalysis(params?: { firmId?: number }): Promise<SalesmanWiseMarginAnalysis[]> {
    const response = await apiClient.get<SalesmanWiseMarginAnalysis[]>("DirectorModule/salesman-wise-margin-analysis", params);
    return response.data;
  },

  async getOnlineDashboard(params?: { firmId?: number }): Promise<DashboardOnlineItem[]> {
    const response = await apiClient.get<DashboardOnlineItem[]>("DirectorModule/dashboard-online", params);
    return response.data;
  },

  async getExpires(params?: { firmId?: number }): Promise<DashboardExpiresResponse> {
    const response = await apiClient.get<DashboardExpiresResponse>("DirectorModule/dashboard-expires", params);
    return response.data;
  },

  async getOutstandingDashboard(params?: { firmId?: number }): Promise<DashboardOutstandingResponse> {
    const response = await apiClient.get<DashboardOutstandingResponse>("DirectorModule/dashboard-outstanding", params);
    return response.data;
  },

  async getRenewDetails(params?: { firmId?: number }): Promise<RenewDetailsResponse> {
    const response = await apiClient.get<RenewDetailsResponse>("DirectorModule/renew-details", params);
    return response.data;
  },

  async getSalesPurchases(params?: { firmId?: number; targetPercentage?: number }): Promise<DashboardSalesPurchasesItem[]> {
    const response = await apiClient.get<DashboardSalesPurchasesItem[]>("DirectorModule/dashboard-sales-purchases-with-target", params);
    return response.data;
  },

  async getSalesPurchasesWithoutTarget(params?: { firmId?: number }): Promise<DashboardSalesPurchasesItem[]> {
    const response = await apiClient.get<DashboardSalesPurchasesItem[]>("DirectorModule/dashboard-sales-purchases", params);
    return response.data;
  },

  async getDashboardSummary(params?: { firmId?: number }): Promise<DashboardSummaryResponse> {
    const response = await apiClient.get<DashboardSummaryResponse>("DirectorModule/dashboard-summary", params);
    return response.data;
  },

  async getDeadStocks(params?: { firmId?: number }): Promise<DeadStockItem[]> {
    const response = await apiClient.get<DeadStockItem[]>("DirectorModule/dead-stocks", params);
    return response.data;
  },
  
/*
  async getMarginAnalysis(params?: { firmId?: number }): Promise<MarginAnalysis[]> {
    try {
      const response = await apiClient.get<MarginAnalysis[]>("DirectorModule/margin-analysis", params);
      return response.data;
    } catch (error: any) {
      // Handle 404 errors gracefully - endpoint might not exist yet
      if (error?.status === 404) {
        return [];
      }
      // Re-throw other errors
      throw error;
    }
  }, */

  async getManufacturerMargins(params?: { firmId?: number }): Promise<ManufacturerMargin[]> {
    const response = await apiClient.get<ManufacturerMargin[]>("DirectorModule/manufacturer-margins", params);
    return response.data;
  },

  async getCustomerMargins(params?: { firmId?: number }): Promise<CustomerMargin[]> {
    const response = await apiClient.get<CustomerMargin[]>("DirectorModule/customer-margins", params);
    return response.data;
  },

  async getMarginSummaryValues(params?: { firmId?: number }): Promise<MarginSummaryValues> {
    const response = await apiClient.get<MarginSummaryValues>("DirectorModule/margin-summary-values", params);
    return response.data;
  },

  async getMarginTrendsMonthlyPerformance(params?: { firmId?: number }): Promise<MarginTrendMonthlyPerformance[]> {
    const response = await apiClient.get<MarginTrendMonthlyPerformance[]>("DirectorModule/margin-trends-monthly-performance", params);
    return response.data;
  },

  async getCreditAgingVsMargin(params?: { firmId?: number }): Promise<CreditAgingVsMargin[]> {
    const response = await apiClient.get<CreditAgingVsMargin[]>("DirectorModule/credit-aging-vs-margin", params);
    return response.data;
  },

  async getPlanDetails(): Promise<PlanDetails[]> {
    const response = await apiClient.get<PlanDetails[]>("DirectorModule/plan-details");
    return response.data;
  },
};

